import { getDoc, setDoc } from './store';
import type { AlertaItem, Cartao, CompraCartao, ContaFin, Financas, Lancamento, Meta, ModId, MsgMestre, PrecoItem, Reg } from './types';
import { addDays, parseYmd, today, uid } from '../lib/date';
import { moedaPadrao } from '../lib/moeda';
import { alertasDosDias } from '../lib/alertasData';
import { t } from '../lib/i18n';
import { mudarCampos, regsDe } from './modulos';
import { falta as faltaParcela, liquido as liquidoReceb, parcelasDe as parcelasDoReceb, quitada as parcelaQuitada, receberParcela, type Parcela } from '../lib/recebimentos';
import { ABAS_ONDE_TRABALHO, diferencaRecebida, ondeTrabalhoLigado, tituloRecebimento, valorLiquido } from '../modulos/ondeTrabalhoBase';

// Finanças (o Mestre Achado dentro do My Planner): contas a pagar e a receber, lançamentos, MEI,
// cartões de crédito, metas, orçamento por categoria e a ligação com Empréstimos, Notas Fiscais e Patrimônio & IR.

const p2 = (n: number) => String(n).padStart(2, '0');
const mut = (fn: (d: Financas) => Financas) => setDoc('financas', fn);
export const moedaDe = (d: Financas) => d.moeda || moedaPadrao();

/** Vencimento de uma conta num mês (dia 31 vira o último dia do mês). */
function noMes(dia: number, y: number, m: number) {
  const ult = new Date(y, m, 0).getDate();
  return `${y}-${p2(m)}-${p2(Math.min(dia, ult))}`;
}

/** Vencimentos de uma conta entre duas datas (inclusive). */
export function ocorrencias(c: ContaFin, de: string, ate: string): string[] {
  if (c.repete === 'nao') return c.venc >= de && c.venc <= ate ? [c.venc] : [];
  const base = parseYmd(c.venc), dia = base.getDate();
  const out: string[] = [];
  const ini = parseYmd(de), fim = parseYmd(ate);
  if (c.repete === 'mensal') {
    for (let y = ini.getFullYear(), m = ini.getMonth() + 1; y < fim.getFullYear() || (y === fim.getFullYear() && m <= fim.getMonth() + 1); m === 12 ? (y++, m = 1) : m++) {
      const v = noMes(dia, y, m);
      if (v >= c.venc && v >= de && v <= ate && (!c.fim || v <= c.fim)) out.push(v);
    }
  } else {
    for (let y = ini.getFullYear(); y <= fim.getFullYear(); y++) {
      const v = noMes(dia, y, base.getMonth() + 1);
      if (v >= c.venc && v >= de && v <= ate && (!c.fim || v <= c.fim)) out.push(v);
    }
  }
  return out;
}

export type Ocorrencia = { conta: ContaFin; venc: string; pago: boolean; atraso: number };
export function ocorrenciasNoPeriodo(contas: ContaFin[], de: string, ate: string): Ocorrencia[] {
  const hoje = today();
  return contas.flatMap(c => ocorrencias(c, de, ate).map(venc => {
    const pago = c.pagos.includes(venc);
    const atraso = !pago && venc < hoje ? Math.round((parseYmd(hoje).getTime() - parseYmd(venc).getTime()) / 864e5) : 0;
    return { conta: c, venc, pago, atraso };
  })).sort((a, b) => a.venc.localeCompare(b.venc));
}
/** Contas em aberto já vencidas (até 1 ano para trás). */
export const atrasadas = (contas: ContaFin[]) => ocorrenciasNoPeriodo(contas, addDays(today(), -365), addDays(today(), -1)).filter(o => !o.pago);

/** Estimativa de multa (2%) e juros (1% ao mês, pro rata) de uma conta em atraso. */
export function encargos(valor: number, dias: number) {
  if (dias <= 0) return 0;
  return Math.round((valor * 0.02 + valor * 0.01 * (dias / 30)) * 100) / 100;
}

export const mesDe = (data: string) => data.slice(0, 7);
export function limitesMes(ym: string) { const [y, m] = ym.split('-').map(Number); return { de: `${ym}-01`, ate: `${ym}-${p2(new Date(y, m, 0).getDate())}` }; }

export function salvarConta(c: ContaFin) {
  mut(d => ({ ...d, moeda: moedaDe(d), contas: d.contas.some(x => x.id === c.id) ? d.contas.map(x => (x.id === c.id ? c : x)) : [...d.contas, c] }));
}
export const excluirConta = (id: string) => mut(d => ({ ...d, contas: d.contas.filter(x => x.id !== id), lancamentos: d.lancamentos.filter(l => !l.id.startsWith(`c:${id}:`)) }));

/** Marca/desmarca o pagamento de um vencimento; o pagamento vira um lançamento do mês (e some ao desmarcar).
 *  valorPago: quanto foi pago de fato (com desconto, ou com multa e juros); sem ele, vale o valor da conta. */
export function alternarPago(c: ContaFin, venc: string, valorPago?: number) {
  const idL = `c:${c.id}:${venc}`;
  mut(d => {
    const conta = d.contas.find(x => x.id === c.id);
    if (!conta) return d;
    const pago = conta.pagos.includes(venc);
    const vp = { ...(conta.valoresPagos ?? {}) };
    if (pago) delete vp[venc]; else if (valorPago != null && Math.abs(valorPago - conta.valor) >= 0.005) vp[venc] = Math.round(valorPago * 100) / 100;
    const contas = d.contas.map(x => (x.id === c.id ? { ...x, pagos: pago ? x.pagos.filter(v => v !== venc) : [...x.pagos, venc], valoresPagos: vp } : x));
    const valor = valorPago ?? conta.valor;
    const lancamentos = pago ? d.lancamentos.filter(l => l.id !== idL)
      : [...d.lancamentos, { id: idL, data: today(), desc: conta.nome, valor: conta.tipo === 'pagar' ? -valor : valor, cat: conta.tipo === 'pagar' ? 'conta' : 'recebimento' }];
    return { ...d, contas, lancamentos };
  });
}
/** Quanto foi pago (ou recebido) de fato num vencimento. */
export const valorPagoDe = (c: ContaFin, venc: string) => c.valoresPagos?.[venc] ?? c.valor;
/** Diferença entre o pago e o valor da conta: negativa = desconto, positiva = multa e juros. */
export const diferencaPaga = (c: ContaFin, venc: string) => (c.pagos.includes(venc) ? Math.round((valorPagoDe(c, venc) - c.valor) * 100) / 100 : 0);
/** Corrige o valor pago de um vencimento já pago (e o lançamento do mês junto). */
export function definirValorPago(c: ContaFin, venc: string, valor: number) {
  const idL = `c:${c.id}:${venc}`;
  const v = Math.round(valor * 100) / 100;
  mut(d => ({ ...d,
    contas: d.contas.map(x => {
      if (x.id !== c.id) return x;
      const vp = { ...(x.valoresPagos ?? {}) };
      if (Math.abs(v - x.valor) < 0.005) delete vp[venc]; else vp[venc] = v;
      return { ...x, valoresPagos: vp };
    }),
    lancamentos: d.lancamentos.map(l => (l.id === idL ? { ...l, valor: c.tipo === 'pagar' ? -v : v } : l)) }));
}

export const novoLancamento = (l: Omit<Lancamento, 'id'>) => mut(d => ({ ...d, moeda: moedaDe(d), lancamentos: [...d.lancamentos, { ...l, id: uid() }] }));
export const excluirLancamento = (id: string) => mut(d => ({ ...d, lancamentos: d.lancamentos.filter(l => l.id !== id) }));
export const setMoeda = (moeda: string) => mut(d => ({ ...d, moeda }));

/** Categorias que não são gasto nem entrada: dinheiro guardado e pagamentos já contados por outra parte
 *  (fatura do cartão cujas compras já entraram, parcela de financiamento já registrada). */
export const NAO_CONTA = new Set(['invest', 'interno']);

/** Entrou, saiu e guardou no mês (investimento não conta como gasto). Inclui as compras do cartão pela fatura
 *  que vence no mês, as parcelas de financiamento pagas e as notas fiscais emitidas já recebidas. */
export function numerosMes(d: Financas, ym: string) {
  const it = [...d.lancamentos.filter(l => mesDe(l.data) === ym), ...virtuaisDoMes(d, ym)];
  const entrou = it.filter(l => l.valor > 0 && !NAO_CONTA.has(l.cat)).reduce((a, l) => a + l.valor, 0);
  const guardou = it.filter(l => l.cat === 'invest').reduce((a, l) => a - l.valor, 0);
  const saiu = it.filter(l => l.valor < 0 && !NAO_CONTA.has(l.cat)).reduce((a, l) => a - l.valor, 0);
  return { entrou, saiu, guardou, saldo: entrou - saiu - guardou, itens: it.sort((a, b) => b.data.localeCompare(a.data)) };
}
/** Gasto de cada categoria no mês. */
export function gastosPorCategoria(itens: Lancamento[]) {
  const m = new Map<string, number>();
  for (const l of itens) if (l.valor < 0 && !NAO_CONTA.has(l.cat)) m.set(l.cat, (m.get(l.cat) ?? 0) - l.valor);
  return m;
}

// ─── MEI (Brasil) ──────────────────────────────────────────────────────────────────────────────
export const setMei = (fn: (m: Financas['mei']) => Financas['mei']) => mut(d => ({ ...d, mei: fn(d.mei) }));
export function meiAno(d: Financas, ano = new Date().getFullYear()) {
  const notas = notasDoAno(ano);
  const fat = d.mei.receitas.filter(r => r.data.startsWith(String(ano))).reduce((a, r) => a + r.valor, 0) + notas;
  const hoje = new Date();
  // 0.77: no comecinho do ano, conta pelo menos 1 mês (antes, uma nota em 2 de janeiro projetava o ano inteiro 180 vezes maior)
  const fracao = ano === hoje.getFullYear() ? Math.max(1 / 12, (hoje.getMonth() + hoje.getDate() / 31) / 12) : 1;
  const projecao = fracao > 0 ? fat / fracao : fat;
  return { fat, notas, projecao, pct: d.mei.limite ? fat / d.mei.limite : 0, pctProj: d.mei.limite ? projecao / d.mei.limite : 0 };
}
export const dasPago = (d: Financas, ym: string) => d.mei.dasPagos.includes(ym);
export const alternarDas = (ym: string) => setMei(m => ({ ...m, dasPagos: m.dasPagos.includes(ym) ? m.dasPagos.filter(x => x !== ym) : [...m.dasPagos, ym] }));

export const getFinancas = () => getDoc('financas');

export const setCategoria = (id: string, cat: string) => mut(d => ({ ...d, lancamentos: d.lancamentos.map(l => (l.id === id ? { ...l, cat } : l)) }));

/** Importa lançamentos do extrato (sem repetir os já importados) e dá baixa nas contas a pagar que baterem
 *  em valor (diferença de até 1%) e data (de 10 dias antes a 7 dias depois do vencimento).
 *  O pagamento de uma fatura de cartão ou de uma parcela de financiamento vira "já contado" (as compras e a
 *  parcela já entram nos números por conta própria) e marca a fatura ou a parcela como paga. */
export function importarExtrato(itens: Lancamento[]) {
  let novos = 0, baixas = 0;
  const parcelasPagas: Reg[] = [];
  mut(d => {
    const ja = new Set(d.lancamentos.map(l => l.id));
    const entram = itens.filter(i => !ja.has(i.id)).map(i => ({ ...i }));
    novos = entram.length;
    let contas = d.contas;
    let faturasPagas = d.faturasPagas ?? [];
    const parc = regs('emprestimos').filter(r => r.col === 'parcela');
    const bate = (a: number, b: number) => Math.abs(a - b) <= Math.max(0.01, b * 0.01);
    for (const it of entram) {
      if (it.valor >= 0) continue;
      const v = -it.valor;
      let achou = false;
      for (const c of contas) {
        if (c.tipo !== 'pagar' || !bate(v, c.valor)) continue;
        const venc = ocorrencias(c, addDays(it.data, -7), addDays(it.data, 10)).find(x => !c.pagos.includes(x));
        if (!venc) continue;
        contas = contas.map(x => (x.id === c.id ? { ...x, pagos: [...x.pagos, venc] } : x));
        baixas++; achou = true;
        break;
      }
      if (achou) continue;
      const fat = faturasNoPeriodo(d, addDays(it.data, -7), addDays(it.data, 10)).find(f => bate(v, f.total));
      if (fat) {
        it.cat = 'interno';
        if (!faturasPagas.includes(`${fat.cartao.id}:${fat.ym}`)) { faturasPagas = [...faturasPagas, `${fat.cartao.id}:${fat.ym}`]; baixas++; }
        continue;
      }
      const p = parc.find(r => r.c.venc && r.c.venc >= addDays(it.data, -7) && r.c.venc <= addDays(it.data, 10) && bate(v, numero(r.c.valorPago ?? r.c.valor)));
      if (p) {
        it.cat = 'interno';
        if (!p.c.pago) { parcelasPagas.push(p); baixas++; }
      }
    }
    return { ...d, moeda: moedaDe(d), contas, faturasPagas, lancamentos: [...d.lancamentos, ...entram] };
  });
  for (const p of parcelasPagas) mudarCampos('emprestimos', p.id, { pago: true, pagoEm: today(), valorPago: p.c.valor });
  return { novos, baixas };
}
/** O lançamento do extrato parece já estar lançado à mão (mesmo valor, até 5 dias de diferença)? */
export function pareceRepetido(d: Financas, it: Lancamento) {
  const de = addDays(it.data, -5), ate = addDays(it.data, 5);
  return d.lancamentos.some(l => !l.id.startsWith('x:') && !l.id.startsWith('f:') && l.data >= de && l.data <= ate
    && Math.abs(l.valor - it.valor) <= Math.max(0.01, Math.abs(it.valor) * 0.01));
}
export const jaImportado = (id: string) => getDoc('financas').lancamentos.some(l => l.id === id);

// ─── Meses ─────────────────────────────────────────────────────────────────────────────────────
export const somaMes = (ym: string, n: number) => { const [y, m] = ym.split('-').map(Number); const d = new Date(y, m - 1 + n, 1); return `${d.getFullYear()}-${p2(d.getMonth() + 1)}`; };
export const mesesEntre = (a: string, b: string) => { const [ya, ma] = a.split('-').map(Number), [yb, mb] = b.split('-').map(Number); return (yb - ya) * 12 + (mb - ma); };
const diaNoMes = (dia: number, ym: string) => { const [y, m] = ym.split('-').map(Number); return noMes(dia, y, m); };
const r2 = (v: number) => Math.round(v * 100) / 100;

// ─── Cartões de crédito ───────────────────────────────────────────────────────────────────────
export const cartoesDe = (d: Financas) => d.cartoes ?? [];
export const comprasDe = (d: Financas) => d.compras ?? [];
/** Mês (YYYY-MM) do vencimento da fatura em que cai uma compra feita nesta data. */
export function faturaDaCompra(c: Cartao, data: string) {
  const [y, m, dd] = data.split('-').map(Number);
  let fech = `${y}-${p2(m)}`;
  if (dd > c.fecha) fech = somaMes(fech, 1);
  return c.vence > c.fecha ? fech : somaMes(fech, 1);
}
export const vencFatura = (c: Cartao, ym: string) => diaNoMes(c.vence, ym);
export const fechaFatura = (c: Cartao, ym: string) => diaNoMes(c.fecha, c.vence > c.fecha ? ym : somaMes(ym, -1));
/** Fatura que ainda vai fechar ou que venceu por último sem passar de hoje: a "atual". */
export function faturaAtual(c: Cartao) {
  const hoje = today();
  const ym = faturaDaCompra(c, hoje);
  return ym;
}
/** Valor da parcela k (0 = primeira); o arredondamento fica na primeira. */
export function valorParcela(cp: CompraCartao, k: number) {
  const base = Math.floor((Math.round(cp.valor * 100) / cp.parcelas) + 1e-6) / 100;
  return k === 0 ? r2(cp.valor - base * (cp.parcelas - 1)) : base;
}
export type ItemFatura = { compra: CompraCartao; k: number; valor: number };
export function itensDaFatura(d: Financas, cartao: string, ym: string): ItemFatura[] {
  return comprasDe(d).filter(cp => cp.cartao === cartao).flatMap(cp => {
    const k = mesesEntre(cp.fatura1, ym);
    return k >= 0 && k < cp.parcelas ? [{ compra: cp, k, valor: valorParcela(cp, k) }] : [];
  }).sort((a, b) => b.compra.data.localeCompare(a.compra.data));
}
export const totalFatura = (d: Financas, cartao: string, ym: string) => r2(itensDaFatura(d, cartao, ym).reduce((s, i) => s + i.valor, 0));
export const faturaPaga = (d: Financas, cartao: string, ym: string) => (d.faturasPagas ?? []).includes(`${cartao}:${ym}`);
export const alternarFatura = (cartao: string, ym: string) => mut(d => {
  const k = `${cartao}:${ym}`, l = d.faturasPagas ?? [];
  return { ...d, faturasPagas: l.includes(k) ? l.filter(x => x !== k) : [...l, k] };
});
/** Quanto do limite está tomado: todas as parcelas de faturas ainda não pagas. */
export function usoDoCartao(d: Financas, c: Cartao) {
  let usado = 0;
  for (const cp of comprasDe(d)) if (cp.cartao === c.id)
    for (let k = 0; k < cp.parcelas; k++) if (!faturaPaga(d, c.id, somaMes(cp.fatura1, k))) usado += valorParcela(cp, k);
  usado = r2(usado);
  return { usado, livre: r2(c.limite - usado), pct: c.limite > 0 ? usado / c.limite : 0 };
}
/** Total das faturas dos próximos meses (o que já está comprometido com parcelas). */
export function proximasFaturas(d: Financas, c: Cartao, n = 6) {
  const ini = faturaAtual(c);
  return Array.from({ length: n }, (_, i) => { const ym = somaMes(ini, i); return { ym, venc: vencFatura(c, ym), total: totalFatura(d, c.id, ym), paga: faturaPaga(d, c.id, ym) }; });
}
export type FaturaAberta = { cartao: Cartao; ym: string; venc: string; total: number; paga: boolean; atraso: number };
/** Faturas que vencem entre duas datas. */
export function faturasNoPeriodo(d: Financas, de: string, ate: string): FaturaAberta[] {
  const out: FaturaAberta[] = [];
  const hoje = today();
  for (const c of cartoesDe(d)) for (let ym = somaMes(de.slice(0, 7), -1); ym <= somaMes(ate.slice(0, 7), 1); ym = somaMes(ym, 1)) {
    const venc = vencFatura(c, ym);
    if (venc < de || venc > ate) continue;
    const total = totalFatura(d, c.id, ym);
    if (total <= 0) continue;
    const paga = faturaPaga(d, c.id, ym);
    out.push({ cartao: c, ym, venc, total, paga, atraso: !paga && venc < hoje ? Math.round((parseYmd(hoje).getTime() - parseYmd(venc).getTime()) / 864e5) : 0 });
  }
  return out.sort((a, b) => a.venc.localeCompare(b.venc));
}
export const faturasAtrasadas = (d: Financas) => faturasNoPeriodo(d, addDays(today(), -365), addDays(today(), -1)).filter(f => !f.paga);

export const salvarCartao = (c: Cartao) => mut(d => ({ ...d, cartoes: cartoesDe(d).some(x => x.id === c.id) ? cartoesDe(d).map(x => (x.id === c.id ? c : x)) : [...cartoesDe(d), c] }));
export const excluirCartao = (id: string) => mut(d => ({ ...d, cartoes: cartoesDe(d).filter(x => x.id !== id), compras: comprasDe(d).filter(x => x.cartao !== id),
  faturasPagas: (d.faturasPagas ?? []).filter(x => !x.startsWith(id + ':')) }));
/** Salva compras do cartão. Com "antesDe" (YYYY-MM), as parcelas de faturas anteriores a esse mês contam como já pagas:
 *  quem cadastra hoje uma compra parcelada antiga (ou importa a fatura com "03/10") já pagou as faturas passadas. */
export const salvarCompras = (lista: CompraCartao[], antesDe?: string) => mut(d => {
  const ids = new Set(lista.map(x => x.id));
  let pagas = d.faturasPagas ?? [];
  if (antesDe) for (const cp of lista) for (let k = 0; k < cp.parcelas; k++) {
    const ym = somaMes(cp.fatura1, k), chave = `${cp.cartao}:${ym}`;
    if (ym < antesDe && !pagas.includes(chave)) pagas = [...pagas, chave];
  }
  return { ...d, moeda: moedaDe(d), faturasPagas: pagas, compras: [...comprasDe(d).filter(x => !ids.has(x.id)), ...lista] };
});
export const excluirCompra = (id: string) => mut(d => ({ ...d, compras: comprasDe(d).filter(x => x.id !== id) }));
export const compraPorId = (id: string) => comprasDe(getDoc('financas')).find(x => x.id === id) ?? null;

// ─── Outras abas: parcelas de financiamento e notas fiscais emitidas ────────────────────────────
const regs = (m: 'emprestimos' | 'notas'): Reg[] => (getDoc(m) as { regs?: Reg[] } | undefined)?.regs ?? [];
const numero = (v: any) => (typeof v === 'number' ? v : Number(String(v ?? '').replace(',', '.')) || 0);
export type ParcelaFin = { p: Reg; contrato: Reg | null; tomei: boolean; nome: string };
export function parcelasDoMes(ym: string): ParcelaFin[] {
  const rs = regs('emprestimos');
  const porId = new Map(rs.map(r => [r.id, r]));
  return rs.filter(r => r.col === 'parcela' && String(r.c.venc ?? '').startsWith(ym)).map(p => {
    const c = p.pai ? porId.get(p.pai) ?? null : null;
    const nome = c ? c.c.nome || [c.c.tipo, c.c.quem].filter(Boolean).join(' · ') : '';
    return { p, contrato: c, tomei: c?.c.sentido !== 'Concedi', nome };
  }).sort((a, b) => String(a.p.c.venc).localeCompare(String(b.p.c.venc)));
}
export const parcelasAtrasadas = () => {
  const hoje = today(), de = addDays(hoje, -365);
  return regs('emprestimos').filter(r => r.col === 'parcela' && !r.c.pago && r.c.venc && r.c.venc < hoje && r.c.venc >= de)
    .flatMap(r => parcelasDoMes(String(r.c.venc).slice(0, 7)).filter(x => x.p.id === r.id));
};
export const alternarParcela = (p: Reg) => mudarCampos('emprestimos', p.id, p.c.pago ? { pago: false, pagoEm: '' }
  : { pago: true, pagoEm: today(), valorPago: p.c.valorPago ?? p.c.valor });

export function notasEmitidasDoMes(ym: string) {
  return regs('notas').filter(r => r.col === 'nota' && r.c.direcao === 'Emitida' && r.c.status !== 'Cancelada' && String(r.c.data ?? '').startsWith(ym));
}
export const notaRecebida = (d: Financas, id: string) => !!d.notasRecebidas?.[id];
export const alternarNota = (id: string) => mut(d => {
  const m = { ...(d.notasRecebidas ?? {}) };
  if (m[id]) delete m[id]; else m[id] = today();
  return { ...d, notasRecebidas: m };
});
/** Faturamento das notas emitidas no ano (para o MEI). */
export const notasDoAno = (ano: number) => regs('notas').filter(r => r.col === 'nota' && r.c.direcao === 'Emitida' && r.c.status !== 'Cancelada'
  && String(r.c.data ?? '').startsWith(String(ano))).reduce((s, r) => s + numero(r.c.valor), 0);

// ─── Onde trabalho (D4): o que as instituições pagam (Professor & Mentoria, Consultório) ───────────
// Cada recebimento fica guardado só na aba de origem. Aqui ele aparece nas contas do mês (a receber) e,
// marcado como recebido (aqui ou lá), entra como entrada calculada (id "t:<recebimento>"): nada é guardado
// em dobro, e marcar ou desmarcar de qualquer lado muda o mesmo registro.
export type RecebimentoFin = { r: Reg; mod: ModId; inst: Reg | null; nome: string };
function recebimentosTrabalho(filtro: (r: Reg) => boolean): RecebimentoFin[] {
  const out: RecebimentoFin[] = [];
  for (const mod of ABAS_ONDE_TRABALHO) {
    if (!ondeTrabalhoLigado(mod)) continue;
    const rs = regsDe(mod), porId = new Map(rs.map(r => [r.id, r]));
    for (const r of rs) {
      if (r.col !== 'recebimento' || !filtro(r)) continue;
      const inst = r.pai ? porId.get(r.pai) ?? null : null;
      out.push({ r, mod, inst, nome: [inst?.c.nome, tituloRecebimento(r, inst)].filter(Boolean).join(' · ') });
    }
  }
  return out.sort((a, b) => String(a.r.c.venc).localeCompare(String(b.r.c.venc)));
}
export const recebimentosDoMes = (ym: string) => recebimentosTrabalho(r => String(r.c.venc ?? '').startsWith(ym));
export const recebimentoPorId = (id: string) => recebimentosTrabalho(r => r.id === id)[0] ?? null;
export const recebimentosAtrasados = () => { const hoje = today(), de = addDays(hoje, -365); return recebimentosTrabalho(r => !r.c.recebido && !!r.c.venc && r.c.venc < hoje && r.c.venc >= de); };
/** Marca (com o valor recebido de fato, Parte B2) ou desmarca o recebimento na aba de origem. */
export const alternarRecebimento = (x: RecebimentoFin, valor?: number) => mudarCampos(x.mod, x.r.id, x.r.c.recebido
  ? { recebido: false, recebidoEm: '', valorRecebido: '' }
  : { recebido: true, recebidoEm: today(), valorRecebido: valor ?? x.r.c.valor, forma: x.r.c.forma || 'PIX' });
export const diferencaDoRecebimento = (x: RecebimentoFin) => diferencaRecebida(x.r);

// ─── Meus clientes (texto 25, D9): o que os clientes pagam entra como "a receber" e "recebido" ───
// As parcelas ficam só no recebimento do cliente; aqui aparecem nas contas do mês e, recebidas, como entrada
// calculada (já sem a taxa da maquininha). Marcar aqui marca lá (é a mesma parcela).
export type ParcelaCliente = { r: Reg; p: Parcela; nome: string };
function parcelasClientes(filtro: (p: Parcela) => boolean): ParcelaCliente[] {
  const rs = regsDe('clientes'), porId = new Map(rs.map(r => [r.id, r]));
  const out: ParcelaCliente[] = [];
  for (const r of rs) {
    if (r.col !== 'receb' || r.c.como === 'À vista') continue;
    const cli = r.pai ? porId.get(r.pai) : undefined;
    for (const p of parcelasDoReceb(r)) if (filtro(p)) out.push({ r, p, nome: [cli?.c.nome, r.c.desc, p.rotulo].filter(Boolean).join(' · ') });
  }
  return out.sort((a, b) => String(a.p.venc).localeCompare(String(b.p.venc)));
}
export const parcelasClientesDoMes = (ym: string) => parcelasClientes(p => !!p.venc && p.venc.startsWith(ym));
export const parcelasClientesAtrasadas = () => { const hoje = today(), de = addDays(hoje, -365); return parcelasClientes(p => !parcelaQuitada(p) && !!p.venc && p.venc < hoje && p.venc >= de); };
export const alternarParcelaCliente = (x: ParcelaCliente, valor?: number) => receberParcela('clientes', x.r.id, x.p.id, parcelaQuitada(x.p) ? 0 : valor ?? null);
export const faltaParcelaCliente = (x: ParcelaCliente) => faltaParcela(x.p);

/** Lançamentos que vêm de outras partes: parcelas do cartão, financiamentos pagos e notas recebidas. */
export function virtuaisDoMes(d: Financas, ym: string): Lancamento[] {
  const out: Lancamento[] = [];
  for (const c of cartoesDe(d)) for (const i of itensDaFatura(d, c.id, ym))
    out.push({ id: `k:${i.compra.id}:${i.k}`, data: i.compra.parcelas > 1 ? vencFatura(c, ym) : i.compra.data, desc: i.compra.desc + (i.compra.parcelas > 1 ? ` (${i.k + 1}/${i.compra.parcelas})` : ''),
      valor: -i.valor, cat: i.compra.cat, origem: 'cartao' });
  const rs = regs('emprestimos'), porId = new Map(rs.map(r => [r.id, r]));
  for (const p of rs) {
    if (p.col !== 'parcela' || !p.c.pago || !String(p.c.pagoEm ?? '').startsWith(ym)) continue;
    const c = p.pai ? porId.get(p.pai) : undefined;
    const v = numero(p.c.valorPago ?? p.c.valor), tomei = c?.c.sentido !== 'Concedi';
    out.push({ id: `e:${p.id}`, data: p.c.pagoEm, desc: `${c ? c.c.nome || c.c.tipo || '' : ''} ${p.c.n}/${p.c.total}`.trim(), valor: tomei ? -v : v, cat: tomei ? 'divida' : 'entrada', origem: 'emprestimo' });
  }
  for (const n of regs('notas')) {
    const quando = d.notasRecebidas?.[n.id];
    if (!quando || !quando.startsWith(ym) || n.c.status === 'Cancelada') continue;
    out.push({ id: `n:${n.id}`, data: quando, desc: [n.c.tipo, n.c.numero, n.c.contraparte].filter(Boolean).join(' · '), valor: numero(n.c.valor), cat: 'entrada', origem: 'nota' });
  }
  // texto 25 (D9): recebido dos clientes (à vista, parcelas, sinal), já sem a taxa da maquininha
  for (const r of regsDe('clientes')) {
    if (r.col !== 'receb') continue;
    const tx = liquidoReceb(r.c).taxa;
    const cli = regsDe('clientes').find(x => x.id === r.pai);
    for (const p of parcelasDoReceb(r)) {
      if (!(Number(p.recebido) > 0) || !String(p.pagoEm ?? '').startsWith(ym)) continue;
      out.push({ id: `cl:${r.id}:${p.id}`, data: p.pagoEm!, desc: [cli?.c.nome, r.c.desc, r.c.como !== 'À vista' ? p.rotulo : ''].filter(Boolean).join(' · '),
        valor: Math.round(Number(p.recebido) * (1 - tx / 100) * 100) / 100, cat: 'entrada', origem: 'clientes' });
    }
  }
  for (const x of recebimentosTrabalho(r => !!r.c.recebido && String(r.c.recebidoEm ?? '').startsWith(ym)))
    out.push({ id: `t:${x.mod}:${x.r.id}`, data: x.r.c.recebidoEm, desc: x.nome, valor: valorLiquido(x.r), cat: 'entrada', origem: 'trabalho' });
  return out;
}

// ─── Orçamento por categoria ──────────────────────────────────────────────────────────────────
export const setOrcamento = (cat: string, v: number) => mut(d => {
  const o = { ...(d.orcamento ?? {}) };
  if (v > 0) o[cat] = r2(v); else delete o[cat];
  return { ...d, orcamento: o };
});
export type Estouro = { cat: string; gasto: number; limite: number; pct: number };
/** Categorias com 80% ou mais do orçamento do mês usado. */
export function estouros(d: Financas, ym: string): Estouro[] {
  const o = d.orcamento ?? {};
  const g = gastosPorCategoria(numerosMes(d, ym).itens);
  return Object.entries(o).filter(([, lim]) => lim > 0).map(([cat, limite]) => ({ cat, limite, gasto: r2(g.get(cat) ?? 0), pct: (g.get(cat) ?? 0) / limite }))
    .filter(e => e.pct >= 0.8).sort((a, b) => b.pct - a.pct);
}

// ─── Metas ─────────────────────────────────────────────────────────────────────────────────────
export const metasDe = (d: Financas) => d.metas ?? [];
export const salvarMeta = (m: Meta) => mut(d => ({ ...d, metas: metasDe(d).some(x => x.id === m.id) ? metasDe(d).map(x => (x.id === m.id ? m : x)) : [...metasDe(d), m] }));
export const excluirMeta = (id: string) => mut(d => ({ ...d, metas: metasDe(d).filter(x => x.id !== id) }));
export function saldoMeta(d: Financas, m: Meta) {
  return r2(m.inicial + d.lancamentos.filter(l => l.id.startsWith(`m:${m.id}:`)).reduce((s, l) => s - l.valor, 0));
}
/** Guardar (valor > 0) ou retirar (valor < 0) dinheiro da meta: vira um lançamento de investimento. */
export const movimentarMeta = (m: Meta, valor: number) => mut(d => ({ ...d, moeda: moedaDe(d),
  lancamentos: [...d.lancamentos, { id: `m:${m.id}:${uid()}`, data: today(), desc: m.nome, valor: -r2(valor), cat: 'invest' }] }));
/** Quanto guardar por mês para chegar no prazo (null sem prazo ou já atingida). */
export function porMesMeta(d: Financas, m: Meta) {
  const falta = m.alvo - saldoMeta(d, m);
  if (falta <= 0 || !m.prazo) return null;
  const meses = Math.max(1, mesesEntre(today().slice(0, 7), m.prazo.slice(0, 7)) + (m.prazo.slice(8, 10) >= today().slice(8, 10) ? 1 : 0));
  return r2(falta / meses);
}

// ─── Cupom do mercado: último preço de cada item ─────────────────────────────────────────────────
export const normItem = (s: string) => String(s).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
export function guardarPrecos(itens: PrecoItem[]) {
  mut(d => {
    const p = { ...(d.precos ?? {}) };
    for (const i of itens) {
      const k = normItem(i.nome);
      if (!k || !(i.preco > 0)) continue;
      if (!p[k] || p[k].data <= i.data) p[k] = { ...i, preco: r2(i.preco) };
    }
    // guarda só os 600 itens mais recentes
    const lista = Object.entries(p).sort((a, b) => b[1].data.localeCompare(a[1].data)).slice(0, 600);
    return { ...d, precos: Object.fromEntries(lista) };
  });
}
/** Procura o último preço de um item da lista de compras (nome igual ou contido). */
export function precoDe(d: Financas, nome: string): PrecoItem | null {
  const k = normItem(nome).replace(/^\d+\s*(x|un|kg|g|l|ml|pct|cx)?\s+/, '');
  if (!k) return null;
  const p = d.precos ?? {};
  if (p[k]) return p[k];
  const achados = Object.entries(p).filter(([n]) => n.includes(k) || (k.length >= 4 && k.includes(n) && n.length >= 4));
  return achados.sort((a, b) => b[1].data.localeCompare(a[1].data))[0]?.[1] ?? null;
}

// ─── Conversa com o Mestre ─────────────────────────────────────────────────────────────────────
export const mensagensMestre = (d: Financas) => d.mestre ?? [];
export const juntarMestre = (msgs: MsgMestre[]) => mut(d => ({ ...d, mestre: [...(d.mestre ?? []), ...msgs].slice(-30) }));
export const limparMestre = () => mut(d => ({ ...d, mestre: [] }));

// ─── Comprovantes e anexos das contas (0.28) ───────────────────────────────────────────────────
export const salvarComprovantes = (id: string, venc: string, lista: import('./types').Anexo[]) => mut(d => ({ ...d, contas: d.contas.map(c => {
  if (c.id !== id) return c;
  const m = { ...(c.comprovantes ?? {}) };
  if (lista.length) m[venc] = lista; else delete m[venc];
  return { ...c, comprovantes: m };
}) }));
export const salvarAnexosConta = (id: string, lista: import('./types').Anexo[]) => mut(d => ({ ...d, contas: d.contas.map(c => (c.id === id ? { ...c, anexos: lista } : c)) }));
/** Todos os comprovantes guardados, do mais novo para o mais antigo. */
export function arquivoDeComprovantes(d: Financas) {
  return d.contas.flatMap(c => Object.entries(c.comprovantes ?? {}).map(([venc, anexos]) => ({ conta: c, venc, anexos })))
    .filter(x => x.anexos.length).sort((a, b) => b.venc.localeCompare(a.venc));
}

// ─── ordem e cópias (0.52) ───────────────────────────────────────────────────────────────────
export const ordenarCartoes = (l: Cartao[]) => mut(d => ({ ...d, cartoes: l }));
export const ordenarMetas = (l: Meta[]) => mut(d => ({ ...d, metas: l }));
const logoDepois = <T extends { id: string }>(l: T[], id: string, novo: T) => { const i = l.findIndex(x => x.id === id); const c = [...l]; c.splice(i + 1, 0, novo); return c; };
export const duplicarCartao = (c: Cartao) => mut(d => ({ ...d, cartoes: logoDepois(cartoesDe(d), c.id, { ...c, id: uid(), nome: t('{titulo} (cópia)', { titulo: c.nome }) }) }));
export const duplicarMeta = (m: Meta) => mut(d => ({ ...d, metas: logoDepois(metasDe(d), m.id, { ...m, id: uid(), nome: t('{titulo} (cópia)', { titulo: m.nome }), inicial: 0, criada: today() }) }));

/** Alertas de cada vencimento da conta (texto 10, A4): os escolhidos; sem escolha, a pagar avisa
 *  os dias de Ajustes › Abas › Finanças (padrão 3) antes e no dia, às 9h; a receber, sem alerta (como antes). */
export function alertasDaConta(c: Pick<ContaFin, 'alertas' | 'tipo'>, antes?: number): AlertaItem[] {
  if (c.alertas) return c.alertas;
  if (c.tipo !== 'pagar') return [];
  let n = antes;
  if (n == null) { try { n = getDoc('ajustes').contasAntes ?? 3; } catch { n = 3; } }
  return alertasDosDias([n]);
}
